import type { ReactNode } from "react";

import { NavGroup, NavLeaf } from "@/components/layout/nav-primitives";
import { isRouteAuthorized, type AppRoute } from "@/lib/routes";
import type { ModuleSection, RecordScope } from "@/lib/module-registry";
import { useAuthSession } from "@/lib/session-context";
import { cn } from "@/lib/utils";

/**
 * The tool rows for the record in scope — hrefs resolved against its id.
 *
 * **The picker in the sidebar header is this group's header** (see
 * `RecordScopePicker`). This component renders no header of its own: doing so
 * printed the record's name twice a few pixels apart, and gave it two "switch
 * record" affordances. Rows render flush with every other top-level nav row —
 * not indented under the picker — matching Market Intelligence's gas "Hubs"
 * row, which never goes through this component (gas has no `recordScope`) and
 * sits flush with Overview by construction. An earlier version indented these
 * rows one level to read as "belonging to" the picker above; in practice it
 * read as these rows being children of whichever row rendered just before
 * this section instead, which is a different and wrong relationship.
 *
 * **Rows persist wherever the scope does**, including on shared records like
 * `/deals` — the picker keeps naming the client, so the rows stay a useful
 * offer of that client's tools rather than a claim about the current page.
 *
 * **Why this is its own component.** `recordScope.useScope` is a hook, and
 * `ModuleSidebar` renders whichever module is active — so calling it in a
 * loop over `mod.sections` would change the hook count whenever the user
 * switches modules. One component per section pins each hook call to a fixed
 * position in its own render tree.
 *
 * Renders nothing when nothing is in scope: every link here needs an id.
 */
export function RecordScopeSection({
  section,
  compact,
  onClose,
}: {
  /**
   * A section carrying `recordScope`. The intersection makes the invariant
   * structural rather than a convention: `useScope` below is then an
   * unconditional call by construction, and a caller passing a non-scoped
   * section is a type error instead of a runtime "Rendered fewer hooks than
   * expected" that unmounts the sidebar.
   */
  section: ModuleSection & { recordScope: RecordScope };
  /** True in the sidebar's icons-only mode. */
  compact: boolean;
  /** Closes the mobile drawer — called on nav tap. */
  onClose: () => void;
}) {
  const session = useAuthSession();
  const scope = section.recordScope;
  const selected = scope.useScope();

  if (!selected) return null;

  // The url segment, which is not always the id — see `toSlug`.
  const slug = scope.toSlug?.(selected.id) ?? selected.id;

  // Resolve each relative segment against the selected record, then gate with
  // the same predicate every other nav row uses — a tool the caller can't
  // reach isn't advertised, exactly as on the tab strip.
  const resolve = (routes: AppRoute[], basePath: string): AppRoute[] =>
    routes
      // Drop rows that do not exist for THIS record before gating on auth —
      // a surface the record hasn't got is not a permissions question.
      .filter((r) => scope.filterRows?.(selected.id, r.path) ?? true)
      .filter((r) =>
        isRouteAuthorized(
          r,
          session.is_super_admin,
          session.features,
          session.permissions,
          session.is_supplier,
        ),
      )
      .map((r) => ({
        ...r,
        path: `${basePath}/${slug}/${r.path}`,
        openOn: r.openOn?.map((p) =>
          p ? `${basePath}/${slug}/${p}` : `${basePath}/${slug}`,
        ),
        section: section.key,
        // A row the scope can expand becomes a NavGroup below. Children come
        // back fully resolved: nesting is the caller's shape, not ours.
        subItems: scope.expandRows?.(selected.id, r.path),
      }));

  const items = resolve(section.routes ?? [], scope.basePath);

  // The record's own tabs, if the scope declares them. Deduped by label
  // against the primary group: Client Profile is the same component on both
  // surfaces, so listing it twice would be two rows to the same place.
  const primaryLabels = new Set(items.map((r) => r.label));
  const secondaryItems = scope.secondary
    ? resolve(scope.secondary.routes, scope.secondary.basePath).filter(
        (r) => !primaryLabels.has(r.label),
      )
    : [];

  if (items.length === 0 && secondaryItems.length === 0) return null;

  // Headings only with room for text and both halves named — one heading
  // alone would read as a title for the whole list.
  const headed = !compact && !!scope.label && !!scope.secondary?.label;

  return (
    <div
      className={cn(
        "flex w-full flex-col",
        compact ? "items-center gap-1" : "mb-2",
        // In icons mode there is no header to group under, so a hairline rule
        // does that work — same treatment the other sections get.
        compact && "mt-2 border-t border-border pt-2",
      )}
    >
      {/* Flush with every other nav row — not indented under the picker
          above. An earlier version indented these one level so they read as
          "belonging to" the picker, but the picker already carries that
          relationship (it names the record, sits right above), and the
          indent read as these rows being children of whichever row happened
          to render just before this section — Market Intelligence's Hubs
          row under Overview, for one. Gas's flat "Hubs" row (no
          `recordScope`, so it never went through this component) sits flush
          with Overview; this makes the region-scoped rows match that. */}
      <div
        className={cn(
          !compact && "flex flex-col",
          compact && "flex flex-col items-center gap-1",
        )}
      >
        {headed && items.length > 0 && <GroupLabel>{scope.label}</GroupLabel>}
        {items.map((item) =>
          item.subItems && item.subItems.length > 0 ? (
            <NavGroup
              key={item.path}
              parent={item}
              onClose={onClose}
              compact={compact}
            />
          ) : (
            <NavLeaf
              key={item.path}
              item={item}
              onClose={onClose}
              compact={compact}
            />
          ),
        )}

        {/* The record's own tabs. Headed when the scope labels both groups
            (Energy Advisor's "Energy Advisor" / "Client"), so it's plain which
            rows are the module's tools and which are the record's own tabs —
            an earlier bare divider left users guessing what the split meant.
            Icons mode, or an unlabeled scope, keeps the hairline. */}
        {secondaryItems.length > 0 && (
          <>
            {headed ? (
              <GroupLabel>{scope.secondary?.label}</GroupLabel>
            ) : (
              <div
                className={cn(
                  "my-1.5 h-px bg-border",
                  compact ? "w-6" : "mx-3",
                )}
                role="separator"
              />
            )}
            {secondaryItems.map((item) => (
              <NavLeaf
                key={item.path}
                item={item}
                onClose={onClose}
                compact={compact}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

/** A group heading inside the scope section — the same eyebrow style as
 *  `ModuleSidebar`'s section labels, so it reads as a sibling of them. */
function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-1 px-2 pt-2 text-xs font-bold uppercase tracking-eyebrow text-text-dim">
      {children}
    </div>
  );
}
